import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Mail, ArrowRight } from 'lucide-react';

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/admin');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
      else setError('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="https://customer-assets.emergentagent.com/job_profile-boost-56/artifacts/9e4tf1j3_Leading%20Presence%20Logo.png" alt="Leading Presence" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="font-['Outfit'] text-3xl font-medium text-slate-900 mb-2">Admin Access</h1>
          <p className="text-slate-500 text-sm">Sign in to manage your dashboard</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm space-y-5" data-testid="admin-login-form">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-600 text-sm" data-testid="login-error">
              {error}
            </div>
          )}
          <div>
            <Label className="text-slate-900 text-sm mb-2 block">Email</Label>
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="admin@leadingpresence.com" className="bg-slate-50 border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 placeholder:text-slate-400 h-11 pl-10" data-testid="login-email" />
            </div>
          </div>
          <div>
            <Label className="text-slate-900 text-sm mb-2 block">Password</Label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={password} onChange={e => setPassword(e.target.value)} type="password" placeholder="Enter password" className="bg-slate-50 border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 placeholder:text-slate-400 h-11 pl-10" data-testid="login-password" />
            </div>
          </div>
          <button type="submit" disabled={loading} className="btn-blue w-full py-3.5 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="login-submit-btn">
            {loading ? 'Signing in...' : 'Sign In'}
            {!loading && <ArrowRight size={16} />}
          </button>
        </form>

        <div className="text-center mt-6">
          <button onClick={() => navigate('/')} className="text-slate-500 text-sm hover:text-blue-600 transition-colors" data-testid="back-to-home">
            Back to website
          </button>
        </div>
      </div>
    </div>
  );
}
